/**
 * SelfieUploadScreen — AvatarFlow step that mirrors the onboarding SelfieIntro
 * visual (camera-icon circle + privacy bullets + gold primary CTA).
 *
 * If the user has previously uploaded a selfie (cached cross-session via
 * `last-selfie.ts`), the screen pre-populates with a thumbnail and a primary
 * "Use this selfie" CTA — one tap and we skip the upload, reusing the cached
 * r2Key. A secondary "Choose another" CTA opens the file picker (which on
 * mobile gives access to the gallery).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUpload } from '../../lib/api-client';
import { useAuth } from '../../store/auth';
import { getLastSelfie, saveLastSelfie, clearLastSelfie } from '../../lib/last-selfie';
import type { LastSelfie } from '../../lib/last-selfie';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import { InlineSelfieCamera } from './InlineSelfieCamera';
import styles from './SelfieUploadScreen.module.css';

const r2KeyStorageKey = (code: string): string => `avatarFlow.r2Key.${code}`;

function writePersistedR2Key(code: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(r2KeyStorageKey(code), value);
    else sessionStorage.removeItem(r2KeyStorageKey(code));
  } catch {
    /* storage unavailable */
  }
}

function CameraIcon(): JSX.Element {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function StarIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15 9 22 9.5 17 14.5 18.5 22 12 18 5.5 22 7 14.5 2 9.5 9 9 12 2" />
    </svg>
  );
}

export interface SelfieUploadScreenProps {
  skuCode: string;
  onUploaded: (r2Key: string) => void;
  testId?: string;
}

export function SelfieUploadScreen({
  skuCode,
  onUploaded,
  testId = 'selfie-upload-screen',
}: SelfieUploadScreenProps): JSX.Element {
  const { t } = useTranslation('flows');
  const jwt = useAuth((s) => s.jwt);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSelfie, setLastSelfie] = useState<LastSelfie | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [pendingSelfie, setPendingSelfie] = useState<{ blob: Blob; url: string; fileName: string } | null>(null);

  useEffect(() => {
    setLastSelfie(getLastSelfie());
  }, []);

  useEffect(() => {
    return () => {
      if (pendingSelfie) URL.revokeObjectURL(pendingSelfie.url);
    };
  }, [pendingSelfie]);

  const uploadBlob = useCallback(
    async (blob: Blob, fileName: string): Promise<void> => {
      if (!jwt) return;
      setUploading(true);
      setError(null);
      try {
        const fd = new FormData();
        fd.append('file', blob, fileName);
        const res = await apiUpload('/api/v1/skus/selfie', jwt, fd);
        if (!res.ok) {
          if (res.status === 403) {
            const body = (await res.json().catch(() => null)) as { error?: string } | null;
            if (body?.error === 'age_gate') {
              setError(t('avatarFlow.error.ageGate'));
              return;
            }
          }
          throw new Error('upload_failed');
        }
        const data = (await res.json()) as { r2Key: string };
        writePersistedR2Key(skuCode, data.r2Key);
        const fileForCache = blob instanceof File
          ? blob
          : new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        await saveLastSelfie({ r2Key: data.r2Key, file: fileForCache });
        onUploaded(data.r2Key);
      } catch {
        setError(t('avatarFlow.error.upload'));
      } finally {
        setUploading(false);
      }
    },
    [jwt, skuCode, onUploaded, t],
  );

  const stagePendingSelfie = useCallback((blob: Blob, fileName: string): void => {
    setPendingSelfie((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return { blob, url: URL.createObjectURL(blob), fileName };
    });
  }, []);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      stagePendingSelfie(file, file.name);
    },
    [stagePendingSelfie],
  );

  const onCameraCapture = useCallback(
    (blob: Blob) => {
      setCameraOpen(false);
      stagePendingSelfie(blob, 'selfie.jpg');
    },
    [stagePendingSelfie],
  );

  const useActive = useCallback((): void => {
    if (pendingSelfie) {
      void uploadBlob(pendingSelfie.blob, pendingSelfie.fileName);
      return;
    }
    if (lastSelfie) {
      writePersistedR2Key(skuCode, lastSelfie.r2Key);
      onUploaded(lastSelfie.r2Key);
    }
  }, [pendingSelfie, lastSelfie, skuCode, onUploaded, uploadBlob]);

  const chooseAnother = useCallback((): void => {
    fileRef.current?.click();
  }, []);

  const openCamera = useCallback((): void => {
    setError(null);
    setCameraOpen(true);
  }, []);

  const closeCamera = useCallback((): void => {
    setCameraOpen(false);
  }, []);

  const onForgetLast = useCallback((): void => {
    if (pendingSelfie) {
      URL.revokeObjectURL(pendingSelfie.url);
      setPendingSelfie(null);
      return;
    }
    clearLastSelfie();
    setLastSelfie(null);
  }, [pendingSelfie]);

  const hasActiveSelfie = pendingSelfie !== null || lastSelfie !== null;
  const activeThumbUrl = pendingSelfie?.url ?? lastSelfie?.dataUrl ?? null;

  if (cameraOpen) {
    return (
      <div className={styles.root} data-testid={testId}>
        <CosmosBackdrop />
        <main className={styles.content}>
          <InlineSelfieCamera onCapture={(b) => void onCameraCapture(b)} onCancel={closeCamera} />
        </main>
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid={testId}>
      <CosmosBackdrop />

      <main className={styles.content}>
        {hasActiveSelfie && activeThumbUrl ? (
          <>
            <div className={styles.thumbWrap}>
              <img
                src={activeThumbUrl}
                alt=""
                className={styles.thumb}
                data-testid="selfie-upload-last-thumb"
              />
              <button
                type="button"
                className={styles.thumbForget}
                onClick={onForgetLast}
                aria-label={t('avatarFlow.upload.forgetLast')}
                data-testid="selfie-upload-forget-last"
              >
                ✕
              </button>
            </div>
            <h1 className={styles.title}>{t('avatarFlow.upload.useLastTitle')}</h1>
            <p className={styles.body}>{t('avatarFlow.upload.useLastBody')}</p>
          </>
        ) : (
          <>
            <div className={styles.iconCircle} aria-hidden="true">
              <CameraIcon />
            </div>
            <h1 className={styles.title}>{t('avatarFlow.upload.title')}</h1>
            <p className={styles.body}>{t('avatarFlow.upload.body')}</p>
            <ul className={styles.reassurance}>
              <li className={styles.line}>
                <span className={styles.lineIcon} aria-hidden="true"><LockIcon /></span>
                <span>{t('avatarFlow.upload.privacyA')}</span>
              </li>
              <li className={styles.line}>
                <span className={styles.lineIcon} aria-hidden="true"><StarIcon /></span>
                <span>{t('avatarFlow.upload.privacyB')}</span>
              </li>
            </ul>
          </>
        )}
      </main>

      <div className={styles.ctaArea}>
        {hasActiveSelfie ? (
          <>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={useActive}
              disabled={uploading}
              aria-busy={uploading || undefined}
              data-testid="selfie-upload-use-last"
            >
              {uploading ? t('avatarFlow.upload.uploading') : t('avatarFlow.upload.useThis')}
            </button>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={openCamera}
              disabled={uploading}
              data-testid="selfie-upload-take-new"
            >
              {t('avatarFlow.upload.takeNew')}
            </button>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={chooseAnother}
              disabled={uploading}
              data-testid="selfie-upload-choose-another"
            >
              {t('avatarFlow.upload.chooseAnother')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.ctaPrimary}
              onClick={openCamera}
              disabled={uploading}
              aria-busy={uploading || undefined}
              data-testid="selfie-upload-take-new"
            >
              {uploading ? t('avatarFlow.upload.uploading') : t('avatarFlow.upload.takeNew')}
            </button>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="selfie-upload-cta"
            >
              {t('avatarFlow.upload.cta')}
            </button>
          </>
        )}
        <p className={styles.hint}>{t('avatarFlow.upload.hint')}</p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={onChange}
        className={styles.hiddenInput}
        data-testid="selfie-upload-file-input"
      />
      {error && (
        <p role="alert" className={styles.error} data-testid="selfie-upload-error">
          {error}
        </p>
      )}
    </div>
  );
}
